import { TrashIcon } from '@phosphor-icons/react';
import { ColumnDef } from '@tanstack/react-table';
import { keyBy, mapValues } from 'lodash';
import { useMemo, useRef } from 'react';
import { useFieldArray, useFormContext } from 'react-hook-form';
import colors from 'tailwindcss/colors';

import { Button } from '@/components/ui/button';
import { Autocomplete, ItemStatusEnum, useApiQuery } from '@/modules/common';
import { PaginatedResponse } from '@/modules/common/types';
import { TextStyleItem } from '@/modules/text-styles/types';

import { TextStyleTabItem } from '..';

const emptyPaginatedResponse: PaginatedResponse<TextStyleItem> = {
  items: [],
  pagination: { page: 1, cursor: '', total: 0 },
};

export const useTextStylesTab = () => {
  const { control, watch } = useFormContext();

  const { data: paginatedData, isFetching } = useApiQuery<PaginatedResponse<TextStyleItem>>(
    '/text-styles?pageSize=1000',
    {
      queryKey: ['textStyles'],
      initialData: emptyPaginatedResponse,
    }
  );

  const data: TextStyleItem[] = useMemo(() => {
    if (Array.isArray(paginatedData)) return paginatedData;
    return (paginatedData as PaginatedResponse<TextStyleItem>)?.items ?? [];
  }, [paginatedData]);

  const textStylesData = useMemo(
    () => data.filter((item) => item.status !== ItemStatusEnum.Archived),
    [data]
  );

  const formattedData = useMemo((): Record<string, TextStyleItem> => {
    if (!data.length) return {};
    return mapValues(keyBy(data, 'id'));
  }, [data]);

  const textStylesOptions = useMemo(
    () => textStylesData.map((ts) => ({ value: ts.name, id: ts.id })),
    [textStylesData]
  );

  const fields = watch('textStyles');

  const { append, update, remove } = useFieldArray({
    control,
    name: 'textStyles',
  });

  const formattedTextStyles: TextStyleTabItem[] = (fields ?? []).map((textStyle) => {
    const formattedTextStyle = formattedData[textStyle.textStyleId];
    return {
      ...textStyle,
      id: formattedTextStyle?.id ?? textStyle.id ?? '',
      name: formattedTextStyle?.name ?? textStyle.name ?? '',
      content: formattedTextStyle?.content ?? textStyle.content ?? '',
    };
  });

  const onEdit = (textStyle: TextStyleTabItem, selectedTextStyle: TextStyleTabItem) => {
    const index = fields.findIndex(
      ({ textStyleId }) => textStyleId === selectedTextStyle.textStyleId
    );
    if (index !== -1) {
      update(index, textStyle);
    }
  };

  const columns: ColumnDef<TextStyleTabItem>[] = [
    {
      size: 200,
      accessorKey: 'name',
      header: 'Name',
      cell: ({ row }) => (
        <Autocomplete
          name={`textStyles.${row.index}.textStyleId`}
          control={control}
          searchable
          placeholder="Select name"
          options={textStylesOptions}
          className="w-[200px] border-0"
          defaultValue={row.getValue('name')}
        />
      ),
    },
    {
      accessorKey: 'content',
      header: 'Content',
      cell: ({ row }) => (
        <span className="line-clamp-1">{row.getValue('content')}</span>
      ),
    },
    {
      size: 40,
      id: 'actions',
      header: 'Action',
      enableHiding: false,
      cell: ({ row }) => (
        <Button
          variant="ghost"
          size="icon"
          data-testid="remove-text-style-button"
          onClick={() => remove(row.index)}
        >
          <TrashIcon size={28} color={colors.zinc[400]} />
        </Button>
      ),
    },
  ];

  return {
    columns,
    formattedTextStyles,
    fields,
    textStylesData,
    isFetching,
    onEdit,
    onAdd: append,
  };
};
